import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { DayOfWeek, Teacher } from '../../types';

const teacherSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(/^07\d{9}$/, 'Invalid UK mobile number (07xxxxxxxxx)'),
  subjects: z.array(z.string()).min(1, 'At least one subject is required'),
  qualifications: z.array(z.string()).min(1, 'At least one qualification is required'),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
  contractType: z.enum(['full-time', 'part-time', 'supply']),
  maxPeriodsPerWeek: z.number().min(1).max(25),
  contractedDays: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])).optional(),
});

type TeacherFormData = z.infer<typeof teacherSchema>;

interface TeacherFormProps {
  teacher?: Teacher;
  onSubmit: (data: TeacherFormData) => void;
  onCancel: () => void;
}

const subjects = [
  'Mathematics',
  'English',
  'Science',
  'Physics',
  'Chemistry',
  'Biology',
  'History',
  'Geography',
  'French',
  'Spanish',
  'German',
  'Art',
  'Music',
  'Drama',
  'PE',
  'ICT',
  'Design Technology',
  'RE',
  'PSHE',
  'Business Studies',
  'Economics',
  'Psychology',
  'Sociology',
];

const qualifications = ['PGCE', 'QTS', 'Bachelor of Education', 'Masters in Education', 'NQT', 'RQT'];

const roles = ['Teacher', 'HoD', 'HoY', 'SLT', 'Deputy Head', 'Head Teacher'];

const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

export const TeacherForm: React.FC<TeacherFormProps> = ({ teacher, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TeacherFormData>({
    resolver: zodResolver(teacherSchema),
    defaultValues: teacher
      ? {
          firstName: teacher.firstName,
          lastName: teacher.lastName,
          email: teacher.email,
          phone: teacher.phone,
          subjects: teacher.subjects,
          qualifications: teacher.qualifications,
          roles: teacher.roles,
          contractType: teacher.contractType,
          maxPeriodsPerWeek: teacher.maxPeriodsPerWeek,
          contractedDays: teacher.contractedDays,
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subjects: [],
          qualifications: [],
          roles: ['Teacher'],
          contractType: 'full-time',
          maxPeriodsPerWeek: 25,
          contractedDays: undefined,
        },
  });

  const contractType = watch('contractType');

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={2}>
        {/* Personal Information */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                type="email"
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Phone"
                fullWidth
                required
                error={!!errors.phone}
                helperText={errors.phone?.message}
                placeholder="07xxxxxxxxx"
              />
            )}
          />
        </Grid>

        {/* Subjects */}
        <Grid item xs={12}>
          <Controller
            name="subjects"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                options={subjects}
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subjects"
                    required
                    error={!!errors.subjects}
                    helperText={errors.subjects?.message}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} size="small" />
                  ))
                }
              />
            )}
          />
        </Grid>

        {/* Qualifications */}
        <Grid item xs={12}>
          <Controller
            name="qualifications"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                options={qualifications}
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Qualifications"
                    required
                    error={!!errors.qualifications}
                    helperText={errors.qualifications?.message}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} size="small" />
                  ))
                }
              />
            )}
          />
        </Grid>

        {/* Roles */}
        <Grid item xs={12}>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.roles}>
                <InputLabel>Roles</InputLabel>
                <Select
                  {...field}
                  multiple
                  value={field.value}
                  onChange={field.onChange}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
                {errors.roles && <FormHelperText>{errors.roles.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        {/* Contract Type */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="contractType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.contractType}>
                <InputLabel>Contract Type</InputLabel>
                <Select {...field} label="Contract Type">
                  <MenuItem value="full-time">Full-time</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                  <MenuItem value="supply">Supply</MenuItem>
                </Select>
                {errors.contractType && <FormHelperText>{errors.contractType.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        {/* Max Periods */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="maxPeriodsPerWeek"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Max Periods per Week"
                type="number"
                fullWidth
                required
                error={!!errors.maxPeriodsPerWeek}
                helperText={errors.maxPeriodsPerWeek?.message}
                onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10))}
              />
            )}
          />
        </Grid>

        {/* Contracted Days (for part-time) */}
        {contractType === 'part-time' && (
          <Grid item xs={12}>
            <Controller
              name="contractedDays"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Contracted Days</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value || []}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Contracted Days" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={dayLabels[value]} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {days.map((day) => (
                      <MenuItem key={day} value={day}>
                        {dayLabels[day]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {/* Form Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {teacher ? 'Update' : 'Create'} Teacher
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
